import { forwardRef, useContext, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { Button, ButtonGroup, MatchMediaContext } from '@george-labs.com/design-system';

import { t } from '@luna/i18n';

import { useImageUploadContext } from '../ImageUploadContext';
import {
    IMAGE_EDITOR_BORDER_SIZE,
    ZOOM_INCREMENT,
    getCanvasWidth,
    getImageEditorSize,
    updateScaleBy,
} from '../ImageUploadEditorService';
import style from './ImageUploadEditor.module.scss';

interface Props {
    imageUrl: string;
}

const COLORS = {
    RED: 255,
    GREEN: 255,
    BLUE: 255,
    PERCENTAGE: 0.7,
};
const INITIAL_SCALE = 1.2;

export const ImageUploadEditor = forwardRef<AvatarEditor, Props>(({ imageUrl }, ref) => {
    const { openErrorModal } = useImageUploadContext();
    const [scale, setScale] = useState(INITIAL_SCALE);
    const { matchingTier } = useContext(MatchMediaContext);

    const handleZoomIn = () => {
        setScale(updateScaleBy(scale, +ZOOM_INCREMENT));
    };

    const handleZoomOut = () => {
        setScale(updateScaleBy(scale, -ZOOM_INCREMENT));
    };

    const editorSize = getImageEditorSize(matchingTier);

    return (
        <div className={style['image-upload-editor-wrapper']}>
            <AvatarEditor
                ref={ref}
                image={imageUrl}
                width={editorSize}
                height={editorSize}
                border={IMAGE_EDITOR_BORDER_SIZE}
                borderRadius={editorSize}
                color={[COLORS.RED, COLORS.GREEN, COLORS.BLUE, COLORS.PERCENTAGE]}
                scale={scale}
                rotate={0}
                onLoadFailure={openErrorModal}
                data-cy="image-upload-change-position"
            />
            <ButtonGroup
                className="mt-2 d-flex justify-content-between"
                // The width changes based on the screen size
                /* eslint-disable-next-line george/jsx-no-inline-styles */
                style={{ width: getCanvasWidth(editorSize) }}
            >
                <Button
                    className="m-0"
                    icon={Button.ICON.ZOOM_OUT}
                    onClick={handleZoomOut}
                    variant={Button.VARIANT.SECONDARY}
                    fullWidth={Button.FULL_WIDTH.NEVER}
                    hiddenLabel={Button.HIDDEN_LABEL.ALWAYS}
                    data-cy="image-upload-zoom-out"
                >
                    {t('main.imageUpload.zoomOut')}
                </Button>
                <Button
                    className="m-0"
                    icon={Button.ICON.ZOOM_IN}
                    onClick={handleZoomIn}
                    variant={Button.VARIANT.SECONDARY}
                    fullWidth={Button.FULL_WIDTH.NEVER}
                    hiddenLabel={Button.HIDDEN_LABEL.ALWAYS}
                    data-cy="image-upload-zoom-in"
                >
                    {t('main.imageUpload.zoomIn')}
                </Button>
            </ButtonGroup>
        </div>
    );
});
